//hooks/useUserDevices.jsx

import { useState, useEffect } from 'react';
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  deleteDoc,
  onSnapshot,
} from 'firebase/firestore';
import { useAuth } from './useAuth';
// import app from '../firebase';
import { db } from "../firebase"
/**
 * This hook handles reading/writing a "devices" subcollection
 * within the current user's Firestore document, e.g.:
 *   /users/{uid}/devices/{deviceId}
 */
const useUserDevices = () => {
  const { currentUser } = useAuth();

  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch devices in real-time
   */
  useEffect(() => {
    if (!currentUser) {
      setDevices([]);
      return;
    }

    setLoading(true);

    const devicesRef = collection(db, 'users', currentUser.uid, 'managed_devices');
    // For advanced usage you could filter or order, e.g. query(devicesRef, where(...))
    const unsubscribe = onSnapshot(
      devicesRef,
      (snapshot) => {
        const deviceList = [];
        snapshot.forEach((docSnap) => {
          deviceList.push({ id: docSnap.id, ...docSnap.data() });
        });
        setDevices(deviceList);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching devices:', err);
        setError('Failed to load devices');
        setLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [currentUser]);

  /**
   * Add a new device to Firestore
   */
  const addDevice = async (deviceData) => {
    if (!currentUser) throw new Error('No current user');
    try {
      const devicesRef = collection(db, 'users', currentUser.uid, 'managed_devices');
      await addDoc(devicesRef, deviceData);
    } catch (err) {
      console.error('Error adding device:', err);
      throw err;
    }
  };

  /**
   * Update an existing device in Firestore
   * @param {string} deviceId - Firestore document id
   * @param {object} deviceData - Updated fields
   */
  const updateDevice = async (deviceId, deviceData) => {
    if (!currentUser) throw new Error('No current user');
    try {
      const deviceDocRef = doc(db, 'users', currentUser.uid, 'managed_devices', deviceId);
      await updateDoc(deviceDocRef, deviceData);
    } catch (err) {
      console.error('Error updating device:', err);
      throw err;
    }
  };

  /**
   * Delete a device
   */
  const deleteDevice = async (deviceId) => {
    if (!currentUser) throw new Error('No current user');
    try {
      const deviceDocRef = doc(db, 'users', currentUser.uid, 'managed_devices', deviceId);
      await deleteDoc(deviceDocRef);
    } catch (err) {
      console.error('Error deleting device:', err);
      throw err;
    }
  };

  return {
    devices,
    loading,
    error,
    addDevice,
    updateDevice,
    deleteDevice
  };
};

export default useUserDevices;
